import { useState, useEffect, useCallback } from 'react'
import {
  Activity, AlertCircle, CheckCircle2, Database, ExternalLink,
  RefreshCw, Server, ChevronDown, ChevronRight, Check, RotateCcw,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchHealthSummary, fetchErrorGroups, fetchPerformanceMetrics, resolveErrorGroup,
  type HealthSummary, type ErrorGroup, type PerformanceSnapshot,
  type ErrorPeriod, type TimeSeriesResult,
} from '../services/adminService'
import StatsCard from '../components/StatsCard'

/**
 * System Health — error tracking and performance, read from Cloud Error
 * Reporting and Cloud Monitoring.
 *
 * This is the "report error-tracking to the main System Admin" half of the
 * observability work: the push half is the Cloud Monitoring webhook and the
 * error digest, both of which land in the notification bell and in email.
 * This page is where an admin goes once one of those has told them to look.
 */

const PERIODS: { value: ErrorPeriod; label: string }[] = [
  { value: 'PERIOD_1_HOUR', label: 'Last hour' },
  { value: 'PERIOD_6_HOURS', label: 'Last 6 hours' },
  { value: 'PERIOD_1_DAY', label: 'Last 24 hours' },
  { value: 'PERIOD_1_WEEK', label: 'Last 7 days' },
  { value: 'PERIOD_30_DAYS', label: 'Last 30 days' },
]

const SERVICES = [
  { value: '', label: 'All surfaces' },
  { value: 'smartwastecycle-api', label: 'API' },
  { value: 'smartwastecycle-admin', label: 'Admin panel' },
  { value: 'smartwastecycle-mobile', label: 'Mobile app' },
]

const SERVICE_LABEL: Record<string, string> = {
  'smartwastecycle-api': 'API',
  'smartwastecycle-admin': 'Admin',
  'smartwastecycle-mobile': 'Mobile',
}

function relativeTime(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** Latest non-zero value across a metric's series. */
function latest(series: TimeSeriesResult[]): number | null {
  for (const s of series) {
    const point = s.points[s.points.length - 1]
    if (point) return point.value
  }
  return null
}

/**
 * A dependency-free sparkline.
 *
 * The panel has no charting library, and adding one for six small trend lines
 * would be the largest change in this feature by a wide margin.
 */
function Sparkline({ points, color = '#16a34a' }: { points: { value: number }[]; color?: string }) {
  if (points.length < 2) return <div className="h-10 flex items-center text-xs text-gray-400">Not enough data</div>

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const path = values
    .map((v, i) => `${(i / (values.length - 1)) * 100},${30 - ((v - min) / span) * 28}`)
    .join(' ')

  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-10" role="img" aria-label="Trend">
      <polyline points={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function MetricCard({
  title, value, unit, series, color,
}: { title: string; value: number | null; unit: string; series: TimeSeriesResult[]; color?: string }) {
  const points = series[0]?.points ?? []
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <p className="text-xs font-medium text-gray-500 mb-1">{title}</p>
      <p className="text-xl font-bold text-gray-900 mb-1">
        {value === null ? '—' : `${value < 1 ? value.toFixed(3) : value.toFixed(value < 10 ? 2 : 0)}${unit}`}
      </p>
      <Sparkline points={points} color={color} />
    </div>
  )
}

export default function SystemHealth() {
  const [summary, setSummary] = useState<HealthSummary | null>(null)
  const [groups, setGroups] = useState<ErrorGroup[]>([])
  const [metrics, setMetrics] = useState<PerformanceSnapshot | null>(null)
  const [period, setPeriod] = useState<ErrorPeriod>('PERIOD_1_DAY')
  const [service, setService] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [metricsError, setMetricsError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    // The summary is the only call that works without GCP access, so it is
    // loaded independently — the page must still render something useful when
    // Error Reporting is unreachable.
    try {
      setSummary(await fetchHealthSummary())
      setForbidden(false)
    } catch (err) {
      setSummary(null)
      // A regular admin who typed the URL gets a wall of zeros otherwise, which
      // reads as "everything is fine" rather than "this is not yours". The API
      // is the boundary; this is only about telling the truth on screen.
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setForbidden(true)
        setGroups([])
        setMetrics(null)
        setLoading(false)
        return
      }
    }

    const [groupResult, metricResult] = await Promise.allSettled([
      fetchErrorGroups({ period, service: service || undefined, pageSize: 25 }),
      fetchPerformanceMetrics(360),
    ])

    setGroups(groupResult.status === 'fulfilled' ? groupResult.value : [])
    if (metricResult.status === 'fulfilled') {
      setMetrics(metricResult.value)
      setMetricsError(null)
    } else {
      setMetrics(null)
      setMetricsError('Metrics are unavailable. Check the API has roles/monitoring.viewer.')
    }
    setLoading(false)
  }, [period, service])

  useEffect(() => { void load() }, [load])

  async function onToggleResolved(group: ErrorGroup) {
    const nextResolved = !group.local?.resolvedAt
    try {
      await resolveErrorGroup(group.groupId, nextResolved)
      setGroups((current) =>
        current.map((g) =>
          g.groupId === group.groupId
            ? { ...g, local: g.local ? { ...g.local, resolvedAt: nextResolved ? new Date().toISOString() : null } : null }
            : g
        )
      )
      toast.success(nextResolved ? 'Marked as handled' : 'Reopened')
    } catch {
      toast.error('Could not update that error group')
    }
  }

  const dbOk = summary?.database === 'connected'
  const p95 = metrics ? latest(metrics.latency.p95) : null
  const errorRate = metrics
    ? metrics.requestsByClass.find((s) => s.label === '5xx')
    : undefined

  if (forbidden) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-lg font-bold text-gray-900">Super Admin access required</h1>
        <p className="text-sm text-gray-500 mt-1">
          System Health shows platform-wide errors and logs across every zone,
          so it is limited to Super Admins.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="text-sm text-gray-500">
            Errors and performance from Google Cloud Observability
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {summary?.consoleUrl && (
            <a
              href={summary.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              Cloud Console
            </a>
          )}
        </div>
      </div>

      {summary && !summary.observabilityAvailable && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
          <p className="text-sm text-amber-800">
            This API is not running on Cloud Run, so Error Reporting and Monitoring
            are unreachable. Error tracking is live only on the deployed backend.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="API"
          value={summary ? 'Reachable' : 'Unknown'}
          icon={Server}
          color={summary ? 'green' : 'orange'}
          subtitle={summary?.revision ? `Revision ${summary.revision}` : undefined}
        />
        <StatsCard
          title="Database"
          value={dbOk ? 'Connected' : 'Disconnected'}
          icon={Database}
          color={dbOk ? 'green' : 'red'}
          subtitle={summary?.region}
        />
        <StatsCard
          title="New errors (24h)"
          value={summary?.newGroups24h ?? 0}
          icon={AlertCircle}
          color={(summary?.newGroups24h ?? 0) > 0 ? 'orange' : 'green'}
          subtitle="Distinct error types first seen"
        />
        <StatsCard
          title="Unresolved"
          value={summary?.unresolvedGroups ?? 0}
          icon={Activity}
          color={(summary?.unresolvedGroups ?? 0) > 0 ? 'red' : 'green'}
          subtitle="Not yet marked handled"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Performance (last 6 hours)</h2>
        {metricsError ? (
          <p className="text-sm text-gray-500">{metricsError}</p>
        ) : metrics ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard title="Latency p50" value={latest(metrics.latency.p50)} unit="ms" series={metrics.latency.p50} />
            <MetricCard title="Latency p95" value={p95} unit="ms" series={metrics.latency.p95} color={p95 && p95 > 3000 ? '#dc2626' : '#16a34a'} />
            <MetricCard title="Latency p99" value={latest(metrics.latency.p99)} unit="ms" series={metrics.latency.p99} />
            <MetricCard
              title="5xx rate"
              value={errorRate ? latest([errorRate]) : 0}
              unit="/s"
              series={errorRate ? [errorRate] : []}
              color="#dc2626"
            />
            <MetricCard title="Instances" value={latest(metrics.instances)} unit="" series={metrics.instances} color="#2563eb" />
          </div>
        ) : (
          <p className="text-sm text-gray-500">Loading metrics…</p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Errors</h2>
          <div className="flex items-center gap-2">
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {SERVICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as ErrorPeriod)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        {loading && groups.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Loading errors…</p>
        ) : groups.length === 0 ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900">No errors in this period</p>
            <p className="text-xs text-gray-500 mt-1">Nothing has been reported for the selected surface.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {groups.map((group) => {
              const isOpen = expanded === group.groupId
              const resolved = !!group.local?.resolvedAt
              return (
                <li key={group.groupId}>
                  <div className="flex items-start gap-3 p-4 hover:bg-gray-50">
                    <button
                      onClick={() => setExpanded(isOpen ? null : group.groupId)}
                      className="mt-0.5 text-gray-400 hover:text-gray-600"
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          {SERVICE_LABEL[group.service ?? ''] ?? 'Unknown'}
                        </span>
                        {resolved && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary-50 text-primary-600">
                            Handled
                          </span>
                        )}
                        {group.local?.lastNotifiedAt && (
                          <span className="text-xs text-gray-400">
                            Alerted {relativeTime(group.local.lastNotifiedAt)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-1 break-words">
                        {group.summary || 'Unknown error'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {group.count} occurrence{group.count === 1 ? '' : 's'}
                        {group.affectedUsers > 0 && ` · ${group.affectedUsers} user${group.affectedUsers === 1 ? '' : 's'}`}
                        {` · last seen ${relativeTime(group.lastSeen)}`}
                        {group.version && ` · ${group.version}`}
                      </p>
                    </div>

                    <button
                      onClick={() => void onToggleResolved(group)}
                      disabled={!group.local}
                      title={group.local ? undefined : 'Recorded once the digest job has seen this group'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                      {resolved ? 'Reopen' : 'Handled'}
                    </button>
                  </div>

                  {isOpen && group.stack && (
                    <pre className="mx-4 mb-4 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-words">
                      {group.stack}
                    </pre>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
